import { useState } from 'react';
import { useNotifier } from '@provider/notifier';
import { PauseIcon, PlayIcon } from '@ui/kit';
import { useAudio, useSelf, useLoaderState, CSSRefTransition } from '@utils/react';
import { containerCls, toggleCls, toggleIconCls, TOGGLE_ICON_TRANSITION_MS, toggleThrobberCls, progressbarCls, timestampCls } from './audio-player.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function AudioPlayer(props) {
  const notifier = useNotifier();
  const audio = useAudio({
    autoplay: true,
    notifier,
    ...props
  });
  const self = useSelf({});
  const [scrubToMs, setScrubToMs] = useState(undefined);
  const showLoader = useLoaderState(audio.timeElapsedMs === undefined, {
    allowInitial: true,
    durationMs: 350
  });
  const handleToggleClick = () => {
    if (audio.isPlaying) {
      audio.pause();
    } else {
      audio.resume();
    }
  };
  const handleProgressPointerDown = evt => {
    if (!evt.isPrimary) {
      return;
    }
    self.isScrubbing = true;
    self.wasPlayingBeforeScrubbing = audio.isPlaying;
    handleProgressPointerMove(evt, {
      avoidPause: true
    });
  };
  const handleProgressPointerUp = () => {
    if (scrubToMs !== undefined) {
      audio.seek(scrubToMs / 1000);
    }
    if (self.wasPlayingBeforeScrubbing) {
      audio.resume();
    }
    self.isScrubbing = false;
    setScrubToMs(undefined);
  };
  const handleProgressPointerMove = (evt, options = {}) => {
    if (!self.isScrubbing || !audio.durationMs) {
      return;
    }
    const bbox = evt.currentTarget.getBoundingClientRect();
    setScrubToMs((evt.pageX - bbox.left) / bbox.width * audio.durationMs);
    if (!options.avoidPause) {
      audio.pause();
    }
  };
  const elapsedTimeMs = scrubToMs ?? audio.timeElapsedMs;
  const isPlaying = self.isScrubbing ? self.wasPlayingBeforeScrubbing ?? false : audio.isPlaying;
  return /*#__PURE__*/jsxs("div", {
    className: containerCls,
    children: [/*#__PURE__*/jsxs("button", {
      className: toggleCls,
      onClick: handleToggleClick,
      children: [/*#__PURE__*/jsx(CSSRefTransition, {
        in: showLoader.current,
        timeout: TOGGLE_ICON_TRANSITION_MS,
        classNames: toggleIconCls,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: toggleThrobberCls
        })
      }), /*#__PURE__*/jsx(CSSRefTransition, {
        in: !showLoader.current && isPlaying,
        timeout: TOGGLE_ICON_TRANSITION_MS,
        classNames: toggleIconCls,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx(PauseIcon, {
          ref: ref,
          className: toggleIconCls,
          height: 22
        })
      }), /*#__PURE__*/jsx(CSSRefTransition, {
        in: !showLoader.current && !isPlaying,
        timeout: TOGGLE_ICON_TRANSITION_MS,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx(PlayIcon, {
          ref: ref,
          className: toggleIconCls,
          height: 22,
          style: {
            marginLeft: "1px"
          }
        })
      })]
    }), /*#__PURE__*/jsx("div", {
      className: progressbarCls,
      onPointerDown: handleProgressPointerDown,
      onPointerMove: handleProgressPointerMove,
      onPointerUp: handleProgressPointerUp,
      children: /*#__PURE__*/jsx("div", {
        style: {
          width: `${(elapsedTimeMs ?? 0) / (audio.durationMs ?? 1) * 100}%`
        }
      })
    }), /*#__PURE__*/jsxs("span", {
      className: timestampCls,
      children: [formatTime(elapsedTimeMs), "\xA0\xA0|\xA0\xA0", formatTime(audio.durationMs)]
    })]
  });
}

/**
 * transform a time given in milliseconds into a human readable mm:ss format
 */
function formatTime(timeMs) {
  if (timeMs === undefined) {
    return "--:--";
  }
  const timeSeconds = Math.round(timeMs / 1000);
  const formatedMinutes = String(Math.floor(timeSeconds / 60)).padStart(2, "0");
  const formatedSeconds = String(timeSeconds % 60).padStart(2, "0");
  return `${formatedMinutes}:${formatedSeconds}`;
}

export { AudioPlayer };
//# sourceMappingURL=audio-player.js.map
